/**
 * HR Engine - Référentiel de Compétences pour Évaluations
 * Page de gestion des compétences conformément au TDR
 */

import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Loader2,
  BookOpen,
  Users,
  Briefcase,
  Star,
  Download,
  Upload,
  Brain,
  Target,
  CheckCircle2,
  Info
} from 'lucide-react';

// Catégories de compétences
const CATEGORIES = {
  technique: { 
    label: 'Technique', 
    color: 'bg-blue-100 text-blue-700',
    icon: Briefcase,
    description: 'Compétences liées au métier et aux outils'
  },
  comportementale: { 
    label: 'Comportementale', 
    color: 'bg-green-100 text-green-700',
    icon: Users,
    description: 'Savoir-être et soft skills'
  },
  manageriale: { 
    label: 'Managériale', 
    color: 'bg-purple-100 text-purple-700',
    icon: Target,
    description: 'Compétences de leadership et gestion'
  }
};

// Niveaux requis
const LEVELS = [
  { value: 1, label: '1 - Insuffisant', color: 'bg-red-100 text-red-700' },
  { value: 2, label: '2 - Moyen', color: 'bg-orange-100 text-orange-700' },
  { value: 3, label: '3 - Bon', color: 'bg-yellow-100 text-yellow-700' },
  { value: 4, label: '4 - Excellent', color: 'bg-green-100 text-green-700' },
];

export default function CompetencyReferencePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [competencies, setCompetencies] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [ratingScale, setRatingScale] = useState({});
  
  // Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingCompetency, setEditingCompetency] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'technique',
    requiredLevel: 3
  });

  useEffect(() => {
    fetchCompetencies();
  }, []);

  const fetchCompetencies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/competency-reference');
      setCompetencies(response.data.competencies || []);
      setByCategory(response.data.byCategory || {});
      setRatingScale(response.data.ratingScale || {});
    } catch (error) {
      console.error('Error fetching competencies:', error);
      toast.error('Erreur lors du chargement des compétences');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Le nom est requis');
        return;
      }
      
      await api.post('/competency-reference', formData);
      toast.success('Compétence créée');
      setShowCreateDialog(false);
      resetForm();
      fetchCompetencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/competency-reference/${editingCompetency.id}`, formData);
      toast.success('Compétence mise à jour');
      setEditingCompetency(null);
      resetForm();
      fetchCompetencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/competency-reference/${deleteConfirm.id}`);
      toast.success('Compétence supprimée');
      setDeleteConfirm(null);
      fetchCompetencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleImportDefaults = async () => {
    try {
      setImporting(true);
      const response = await api.post('/competency-reference/import-defaults');
      toast.success(response.data.message);
      fetchCompetencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'technique',
      requiredLevel: 3
    });
  };

  const openEdit = (comp) => {
    setFormData({
      name: comp.name,
      description: comp.description || '',
      category: comp.category,
      requiredLevel: comp.requiredLevel
    });
    setEditingCompetency(comp);
  };

  // Filtrer les compétences
  const filteredCompetencies = competencies.filter(comp => {
    if (categoryFilter !== 'all' && comp.category !== categoryFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return comp.name.toLowerCase().includes(query) || 
             comp.description?.toLowerCase().includes(query);
    }
    return true;
  });

  // Statistiques
  const stats = {
    total: competencies.length,
    technique: competencies.filter(c => c.category === 'technique').length,
    comportementale: competencies.filter(c => c.category === 'comportementale').length,
    manageriale: competencies.filter(c => c.category === 'manageriale').length,
  };

  if (loading) {
    return (
      <DashboardLayout title="Référentiel de Compétences">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Référentiel de Compétences">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BookOpen className="h-7 w-7 text-purple-500" />
              Référentiel de Compétences
            </h1>
            <p className="text-slate-500 mt-1">
              Gérez les compétences utilisées pour les évaluations de performance
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {competencies.length === 0 && (
              <Button 
                variant="outline" 
                onClick={handleImportDefaults}
                disabled={importing}
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Importer les compétences par défaut
              </Button>
            )}
            <Button onClick={() => setShowCreateDialog(true)} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle compétence
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <BookOpen className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
                <p className="text-sm text-slate-500">Total compétences</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-blue-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <Briefcase className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                <div className="text-2xl font-bold text-blue-600">{stats.technique}</div>
                <p className="text-sm text-slate-500">Techniques</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <Users className="w-8 h-8 mx-auto text-green-500 mb-2" />
                <div className="text-2xl font-bold text-green-600">{stats.comportementale}</div>
                <p className="text-sm text-slate-500">Comportementales</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-purple-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <Target className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                <div className="text-2xl font-bold text-purple-600">{stats.manageriale}</div>
                <p className="text-sm text-slate-500">Managériales</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Rating Scale Info */}
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">Barème de notation (1-4)</p>
                <div className="flex flex-wrap gap-3 mt-2">
                  {Object.entries(ratingScale).map(([key, value]) => (
                    <Badge key={key} variant="outline" className={LEVELS.find(l => l.value === parseInt(key))?.color}>
                      {key}: {value.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Rechercher une compétence..." 
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              <SelectItem value="technique">Technique</SelectItem>
              <SelectItem value="comportementale">Comportementale</SelectItem>
              <SelectItem value="manageriale">Managériale</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Competencies Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Compétence</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>Niveau requis</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCompetencies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12">
                    <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">
                      {competencies.length === 0 
                        ? 'Aucune compétence dans le référentiel' 
                        : 'Aucune compétence ne correspond aux filtres'
                      }
                    </p>
                    {competencies.length === 0 && (
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={handleImportDefaults}
                        disabled={importing}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Importer les compétences par défaut
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredCompetencies.map((comp) => {
                  const cat = CATEGORIES[comp.category] || CATEGORIES.technique;
                  const CatIcon = cat.icon;
                  const level = LEVELS.find(l => l.value === comp.requiredLevel);
                  
                  return (
                    <TableRow key={comp.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${cat.color.replace('text-', 'bg-').split(' ')[0]}`}>
                            <CatIcon className={`w-5 h-5 ${cat.color.split(' ')[1]}`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{comp.name}</p>
                            {comp.description && (
                              <p className="text-sm text-slate-500 line-clamp-1">{comp.description}</p>
                            )}
                            {comp.isDefault && (
                              <Badge variant="outline" className="text-xs mt-1">Par défaut</Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={cat.color}>{cat.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={level?.color}>
                          {level?.label || `Niveau ${comp.requiredLevel}`}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => openEdit(comp)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => setDeleteConfirm(comp)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showCreateDialog || !!editingCompetency} onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setEditingCompetency(null);
            resetForm();
          }
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingCompetency ? 'Modifier la compétence' : 'Nouvelle compétence'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nom de la compétence *</Label>
                <Input
                  placeholder="Ex: Maîtrise technique du poste"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Décrivez cette compétence..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({...formData, category: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORIES).map(([key, cat]) => (
                        <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Niveau requis</Label>
                  <Select 
                    value={formData.requiredLevel.toString()} 
                    onValueChange={(value) => setFormData({...formData, requiredLevel: parseInt(value)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEVELS.map((level) => (
                        <SelectItem key={level.value} value={level.value.toString()}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCreateDialog(false);
                  setEditingCompetency(null);
                  resetForm();
                }}
              >
                Annuler
              </Button>
              <Button 
                onClick={editingCompetency ? handleUpdate : handleCreate}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {editingCompetency ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cette compétence ?</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer la compétence "{deleteConfirm?.name}" ?
                Cette action ne supprimera pas les évaluations existantes qui utilisent cette compétence.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
